"use client"

import { chakra, RecipeVariantProps, HTMLChakraProps, Image } from "@chakra-ui/react"
import { buttonRecipe } from "./button.recipe"
import { ReactElement, ReactNode } from "react"

type ButtonVariantProps = RecipeVariantProps<typeof buttonRecipe>

export interface ButtonProps extends HTMLChakraProps<"button", ButtonVariantProps> {
  icon?: ReactElement | string;
  iconPlacement?: 'before' | 'after';
  'data-e2e'?: string;
}

const BaseButton = chakra("button", buttonRecipe)

/**
 * @useFor the canonical action button — primary, secondary, danger,
 *   ghost variants via the `buttonRecipe`. Optional icon (string URL
 *   or React element) before or after the label.
 *
 *   Examples in-tree: every form Save / Cancel, table toolbar
 *   actions, drawer / dialog primary actions.
 *
 *   For the "+" add button common to admin tables use `<AddButton>`
 *   instead (different visual treatment). For icon-only buttons,
 *   pair this with `<Tooltip>` for the accessible label. For
 *   stateful toggle buttons use `<ToggleGroup>` or `<TableOptions>`.
 */
export const Button = ({ icon, iconPlacement = 'before', children, ...props }: ButtonProps) => {
  const renderIcon = () => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <Image src={icon} width="16px" height="16px" />;
    }
    return icon;
  };

  const iconElement = renderIcon();

  return (
    <BaseButton {...props}>
      {iconElement && iconPlacement === 'before' && iconElement}
      {children as ReactNode}
      {iconElement && iconPlacement === 'after' && iconElement}
    </BaseButton>
  )
}
